import { useEffect, useRef, useState } from 'react';
import { css, cx } from '@emotion/css';
import { CodeInput } from '../../shared/components/CodeInput';
import { h1Style, p2Style } from '../../shared/styles/text';
import {
  Button,
  ButtonVariantEnum,
  ButtonSizeEnum,
} from '../../shared/components/Button';
import { Colors } from '../../shared/styles/colors';
import { useSendVerificationCode } from '../../shared/services/api/user';
import { Layout } from '../../shared/components/layout/Layout';
import { authenticationStore } from '../../shared/stores/authentification-store';
import { useQueryClient } from '@tanstack/react-query';
import { CacheKeys } from '../../shared/services/api/cache-configuration';
import { StepComponentProps } from '../../shared/components/Stepper';
import { observer } from 'mobx-react';
import { useNavigator } from '../../routing/navigator';
import {
  authButtonContainerStyle,
  authFormContentStyle,
} from '../../shared/styles/auth-layout';
import { codeInputStyle } from '../../shared/styles/code-input';

export const VerificationStep = observer(
  (props: StepComponentProps): JSX.Element => {
    const resendTimerValue = 10;
    const [errorState, setErrorState] = useState(false);
    const [resendEmailState, setResendEmailState] = useState(false);
    const [resendTimer, setResendTimer] = useState(resendTimerValue);
    const [verificationCode, setVerificationCode] = useState<number>(0);
    const [verifying, setVerifying] = useState(false);
    const intervalRef: any = useRef(null);
    const sendVerificationCode = useSendVerificationCode();
    const queryClient = useQueryClient();
    const { setCurrentStep } = props;
    const authData = authenticationStore.getAuthenticationData();
    const navigator = useNavigator();

    const onSubmit = async () => {
      try {
        setVerifying(true);
        if (!authData?.email) {
          navigator.toCheckEmail();
        } else {
          const { token } = await sendVerificationCode({
            code: verificationCode,
            email: authData.email,
          });
          if (token) {
            authenticationStore.setUserIsVerified(true);
            window.localStorage.setItem('token', token);
            await queryClient.invalidateQueries({ queryKey: [CacheKeys.ME] });
            authenticationStore.reset();
            navigator.toSuccessful();
          } else {
            setErrorState(true);
          }
        }
      } catch {
        setErrorState(true);
      } finally {
        setVerifying(false);
      }
    };

    const resendEmailInterval = () => {
      intervalRef.current = setInterval(() => {
        setResendTimer((resendTimer) => resendTimer - 1);
      }, 1000);
    };

    const resendEmail = () => {
      setResendEmailState(true);
      resendEmailInterval();
    };

    useEffect(() => {
      return () => {
        if (resendTimer <= 1) {
          clearInterval(intervalRef.current);
          setResendTimer(resendTimerValue);
          setResendEmailState(false);
        }
      };
    }, [resendTimer]);

    return (
      <Layout.Content className={authFormContentStyle}>
        <div className={verificationContentStyle}>
          <h1 className={cx(h1Style, headerStyle)}>Verify your email.</h1>
          <p className={cx(p2Style, paragraphStyle)}>
            We have sent a code to your email.
          </p>
          <CodeInput
            fields={4}
            className={codeInputStyle(errorState)}
            fieldHeight={56}
            fieldWidth={52}
            onComplete={(value: string) => setVerificationCode(parseInt(value))}
          />
          <div className={infoTextStyle}>
            <p
              className={cx(p2Style, inputErrorStyle(errorState))}
              role="alert"
            >
              Wrong code entered. Please try again.
            </p>
            <p className={cx(p2Style, resendTextStyle(resendEmailState))}>
              Email sent. You can resend it in {resendTimer} seconds.
            </p>
          </div>
          <Button
            text={'resend email'}
            type={'button'}
            variant={ButtonVariantEnum.TEXT}
            size={ButtonSizeEnum.LARGE}
            onClick={resendEmail}
            disabled={resendEmailState}
          />
        </div>
        <div className={authButtonContainerStyle}>
          <Button
            variant={ButtonVariantEnum.TEXT}
            size={ButtonSizeEnum.LARGE}
            text={'back'}
            onClick={() => {
              setCurrentStep(0);
            }}
          />
          <Button
            variant={ButtonVariantEnum.FILLED}
            size={ButtonSizeEnum.LARGE}
            text={'verify'}
            type={'submit'}
            onClick={onSubmit}
            loading={verifying}
          />
        </div>
      </Layout.Content>
    );
  },
);

const verificationContentStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 2rem;
`;

const headerStyle = css`
  margin-bottom: 0.75rem;
  align-self: flex-start;
`;

const paragraphStyle = css`
  margin-bottom: 2.5rem;
  align-self: flex-start;
  color: ${Colors.TEXT_SECONDARY};
`;

const infoTextStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: 6rem;
  text-align: center;
`;

const inputErrorStyle = (errorState: any) => css`
  color: ${Colors.ERROR_DARK};
  margin-top: 1rem;
  ${errorState === false &&
  `
    visibility: hidden;
  `}
`;

const resendTextStyle = (resendEmail: any) => css`
  color: ${Colors.TEXT_SECONDARY};
  margin-top: 0.5rem;
  ${resendEmail === false &&
  `
    visibility: hidden;
  `}
`;
